import { date } from "quasar"
import { isRxDocument } from "rxdb"
import { i18n } from "src/boot/i18n"
import { IssuePriorityDocument } from "src/db/collectionDefinitions/csi/issuePriorities"
import { getDB } from "src/db/databases"
import router from "src/router"
import { useCacheStore } from "src/stores/cacheStore"
import { useUserStore } from "src/stores/userStore"
import { Log } from "src/util/classes/log"
import { Icons } from "src/util/enums/fontAwesomeIcons"
import { NamedManagerPath } from "src/util/enums/namedPaths"
import {
  closeIssueCorrectedDialog,
  closeIssueNotCorrectedDialog,
  rejectIssueDialog,
  workMustBeReviewedDialog,
} from "src/util/functions/dialogs"
import {
  getIssuePriorityListData,
  getWorkDomainWorkspaceListData,
} from "src/util/functions/entityData"
import { getIssuePriorityColor, getIssuePriorityData } from "src/util/functions/issuePriority"
import { getItemData } from "src/util/functions/item"
import { getItemClassData } from "src/util/functions/itemClass"
import {
  showErrorNotification,
  showNotImplementedNotification,
  showSuccessNotification,
  showWarningNotification,
  simpleUndoNotification,
} from "src/util/functions/notifications"
import { getTemporalInfo } from "src/util/functions/time"
import { getWorkDomainWorkspaceData } from "src/util/functions/workDomain"
import {
  getWorkOrderCorrectiveItemData,
  userHasPermissionToUpdateWorkOrder,
} from "src/util/functions/workOrder"
import {
  getWorkOrderPriorityColor,
  getWorkOrderPriorityData,
} from "src/util/functions/workOrderPriority"
import { getEntityHistory } from "src/util/http/csi/history"
import {
  getIssueOnItemComment,
  getIssueOnItemCommentList,
  getIssuesOnItem,
  getIssuesOnItemActionsList,
  patchIssuesOnItem,
  postIssueOnItemComment,
} from "src/util/http/csi/issues"
import { IconRowElement } from "src/util/types/components/sfxIconRow"
import { IconTableRowElement, RowData } from "src/util/types/components/sfxTable"
import { AttachmentUpload } from "src/util/types/dataModels/attachment"
import { PayloadEntity } from "src/util/types/dataModels/genericObject"
import {
  ClosedIssueData,
  Issue,
  IssueCardData,
  IssueDocument,
  IssuesOnItem,
  IssueTableData,
  RejectIssueData,
} from "src/util/types/dataModels/issue"
import { IssueComment, IssueCommentPayload } from "src/util/types/dataModels/issueComment"
import { IssuePriority } from "src/util/types/dataModels/issuePriority"
import { CorrectiveWorkOnItem } from "src/util/types/dataModels/performedWork"
import {
  PERMISSION_ACCESS_CREATE,
  PERMISSION_ACCESS_DELETE,
  PERMISSION_ACCESS_UPDATE,
  PERMISSION_ISSUES,
} from "src/util/types/dataModels/user"
import { WorkOrderPriority } from "src/util/types/dataModels/workOrderPriority"
import { Item } from "../types/dataModels/item"
import { filterInvalidWorkDomains, getRegularWorkOnItemData } from "./performedWork"

export function userHasPermissionToCreateIssue(): boolean {
  const userStore = useUserStore()

  return userStore.checkUserPermission(PERMISSION_ISSUES, PERMISSION_ACCESS_CREATE)
}

export function userHasPermissionToUpdateIssue(issue: IssuesOnItem): boolean {
  const userStore = useUserStore()

  return userStore.checkUserPermission(PERMISSION_ISSUES, PERMISSION_ACCESS_UPDATE, {
    workDomainUUID: issue.work_domain,
  })
}

export async function getIssueData(issueId: string): Promise<IssuesOnItem | undefined> {
  try {
    const cacheStore = useCacheStore()
    const issueInCache = cacheStore.issues_on_item.get(issueId)

    if (issueInCache) {
      return issueInCache
    } else {
      const db = await getDB()
      const issueInDb = await db.issues_on_item.findOne(issueId).exec()

      if (issueInDb) {
        const mutableIssue = issueInDb.toMutableJSON()
        cacheStore.issues_on_item.set(mutableIssue.id, mutableIssue)
        return mutableIssue
      } else {
        return await getIssuesOnItem({}, issueId)
      }
    }
  } catch (error) {
    Log.error({ message: "An error occurred while fetching Issue data", error })
  }
}

export function getOverdueTime(issue: Issue, priority: IssuePriority): number {
  // The issue is processed, they don't need to have overdue time
  if (issue.concluded_dt) return Number.NEGATIVE_INFINITY

  const currentDate = new Date()

  const deadline = date.addToDate(new Date(issue.declared_dt || issue.created_dt), {
    days: priority?.days_to_correct || Number.POSITIVE_INFINITY,
  })

  return currentDate.getTime() - deadline.getTime()
}

export function setOverdueTime(issue: IssueCardData) {
  issue.overdueTime = getOverdueTime(issue.issueData, issue.priorityData)
}

export async function getIssueCardData(issueDoc: IssuesOnItem): Promise<IssueCardData> {
  const cacheStore = useCacheStore()

  let priorityData = cacheStore.issue_priority.get(issueDoc.priority)
  if (!priorityData) {
    const db = await getDB()
    const priority = await db.issue_priority.findOne({ selector: { id: issueDoc.priority } }).exec()

    priorityData = priority?.toMutableJSON()
  }

  if (!priorityData) {
    Log.error({
      message: "Could not find priority for the Issue",
      data: { issue: issueDoc.id, priority: issueDoc.priority },
    })

    throw "No priority found"
  }

  let workOrderData
  if (issueDoc.corrective_work_order) {
    workOrderData = await getWorkOrderCorrectiveItemData(issueDoc.corrective_work_order)
  }

  let workOrderPriorityData
  if (workOrderData) {
    workOrderPriorityData = await getWorkOrderPriorityData(workOrderData.priority)
  }

  const correctiveWork = await getAcceptedOrUnreviewedCorrectiveWorkOnItemForIssue(issueDoc.id)

  return {
    issueData: cacheStore.issues_on_item.get(issueDoc.id) || issueDoc,
    actionableItem: await getItemData(issueDoc.actionable_item),
    priorityData,
    correctiveWorkOrderData: workOrderData,
    workOrderPriorityData,
    correctiveWork: correctiveWork,
  }
}

export async function getHighestPriorityFromIssueList(
  list: IssueDocument[] | Issue[]
): Promise<IssuePriorityDocument | null> {
  const db = await getDB()
  const ids = list.map((issue) => issue.priority)

  const prioritiesMap = await db.issue_priority.findByIds(ids).exec()
  if (prioritiesMap.size === 0) return null
  const priorities = Array.from(prioritiesMap.values()).map((value) => value)

  return priorities.reduce((prev, current) =>
    prev.risk_factor > current.risk_factor ? prev : current
  )
}

function getLatestIssueFromCache(itemId: string) {
  const cacheStore = useCacheStore()

  const issuesInCache = cacheStore.issues_on_item.values()
  const issuesOnItem = []

  for (const issue of issuesInCache) {
    if (issue.actionable_item === itemId) {
      issuesOnItem.push(issue)
    }
  }

  if (issuesOnItem.length === 0) return

  return issuesOnItem.reduce((a, b) =>
    (a.declared_dt || a.created_dt) > (b.declared_dt || b.created_dt) ? a : b
  )
}

export async function getLatestIssueOnItem(itemId: string) {
  const cacheStore = useCacheStore()

  if (cacheStore.issues_on_item.size > 0) {
    return getLatestIssueFromCache(itemId)
  } else {
    const db = await getDB()
    const issuesOnItemInDb = await db.issues_on_item
      .find({
        selector: { actionable_item: itemId },
      })
      .exec()

    if (issuesOnItemInDb.length === 0) return

    const latestIssue = issuesOnItemInDb.reduce((a, b) =>
      (a.declared_dt || a.created_dt) > (b.declared_dt || b.created_dt) ? a : b
    )
    return latestIssue.toMutableJSON()
  }
}

/**
 * Sort issues on item based on the risk factor
 *
 * @param a
 * @param b
 * @param dir
 * <ul>
 *   <li><b> 1 </b> ascending </li>
 *   <li><b> -1 </b> descending </li>
 * </ul>
 */
export function sortIssuesOnPriority(a: IssueCardData, b: IssueCardData, dir: -1 | 1): number {
  return (b.priorityData?.risk_factor - a.priorityData?.risk_factor) * dir
}

/***
 * Sort issues on item based on the declared_dt. If declared_dt is not available function uses created_dt
 *
 * @param a
 * @param b
 * @param dir
 * <ul>
 *   <li><b> 1 </b> ascending </li>
 *   <li><b> -1 </b> descending </li>
 * </ul>
 */
export function sortIssuesOnRaised(a: IssueTableData, b: IssueTableData, dir: -1 | 1): number {
  return (
    (new Date(b.issueData.declared_dt || b.issueData.created_dt).getTime() -
      new Date(a.issueData.declared_dt || a.issueData.created_dt).getTime()) *
    dir
  )
}

/**
 * Sort the items on:
 * 1. Overdue time
 * 2. Issue priority
 * 3. Work Order priority
 * 3. Created/Raised Date
 *
 * @param a
 * @param b
 */
export function sortOpenIssuesData(
  a: IssueCardData | IssueTableData,
  b: IssueCardData | IssueTableData
): number {
  return (
    (b.overdueTime || 0) - (a.overdueTime || 0) ||
    (b.priorityData?.risk_factor || 0) - (a.priorityData?.risk_factor || 0) ||
    (b.workOrderPriorityData?.rank || 0) - (a.workOrderPriorityData?.rank || 0) ||
    new Date(b.issueData.declared_dt || b.issueData.created_dt).getTime() -
      new Date(a.issueData.declared_dt || a.issueData.created_dt).getTime()
  )
}

/**
 * Sort the issues on:
 * 1. Closed time
 * 2. Issue priority
 * 3. Work Order priority
 * 3. Created/Raised Date
 *
 * @param a
 * @param b
 */
export function sortClosedIssuesData(
  a: IssueCardData | IssueTableData,
  b: IssueCardData | IssueTableData
): number {
  return (
    new Date(b.issueData.concluded_dt || 0).getTime() -
      new Date(a.issueData.concluded_dt || 0).getTime() ||
    (b.priorityData?.risk_factor || 0) - (a.priorityData?.risk_factor || 0) ||
    (b.workOrderPriorityData?.rank || 0) - (a.workOrderPriorityData?.rank || 0) ||
    new Date(b.issueData.declared_dt || b.issueData.created_dt).getTime() -
      new Date(a.issueData.declared_dt || a.issueData.created_dt).getTime()
  )
}

/**
 * Sort the issues first by open status {@link sortOpenIssuesData},
 * then by closed status {@link sortClosedIssuesData}.
 *
 * This is applicable for both card and table data.
 *
 * @param a
 * @param b
 */
export function sortIssueData(
  a: IssueCardData | IssueTableData,
  b: IssueCardData | IssueTableData
): number {
  if (!a.issueData.concluded_dt && !b.issueData.concluded_dt) {
    return sortOpenIssuesData(a, b)
  } else if (a.issueData.concluded_dt && b.issueData.concluded_dt) {
    return sortClosedIssuesData(a, b)
  } else {
    return a.issueData.concluded_dt ? 1 : -1
  }
}

/**
 * Returns back the appropriate time for issue cards.
 * If the issue is overdue, it would return back the deadline for correction.
 * Else, it will return the time since raised.
 *
 * @param issue
 */
export function getIssueCardTime(issue: IssueCardData) {
  let dateTime = new Date(issue.issueData.declared_dt || issue.issueData.created_dt).toJSON()

  if (issue.overdueTime && issue.overdueTime > 0) {
    const daysToCorrect = issue.priorityData?.days_to_correct

    // Issue Priority has days to correct (some of them don't have)
    if (daysToCorrect) {
      dateTime = date
        .addToDate(dateTime, {
          days: daysToCorrect,
        })
        .toJSON()
    }
  }

  return getTemporalInfo(dateTime, "card")
}

export async function getOpenCorrectiveWorkOnItemForIssue(
  issueId: string
): Promise<CorrectiveWorkOnItem | undefined> {
  try {
    const cacheStore = useCacheStore()

    if (cacheStore.corrective_work_on_item.size > 0) {
      for (const correctiveWork of cacheStore.corrective_work_on_item.values()) {
        if (correctiveWork.issue === issueId && !correctiveWork.conclusion) {
          return correctiveWork
        }
      }
    } else {
      const db = await getDB()
      const correctiveWorkOnItemDoc = await db.corrective_work_on_item
        .findOne({
          selector: {
            issue: issueId,
            conclusion: null,
          },
        })
        .exec()

      if (correctiveWorkOnItemDoc) {
        const correctiveWorkOnItemMutable = correctiveWorkOnItemDoc.toMutableJSON()
        cacheStore.corrective_work_on_item.set(
          correctiveWorkOnItemMutable.id,
          correctiveWorkOnItemMutable
        )
        return correctiveWorkOnItemMutable
      } else {
        return await getOpenCorrectiveWorkOnItemFromServer(issueId)
      }
    }
  } catch (error) {
    Log.error({
      message: "An error occurred while fetching open CorrectiveWorkOnItem data",
      error,
    })
  }
}

export async function getAcceptedOrUnreviewedCorrectiveWorkOnItemForIssue(
  issueId: string
): Promise<CorrectiveWorkOnItem | undefined> {
  try {
    const cacheStore = useCacheStore()

    // Corrective work is;
    // accepted when `correctiveWork.conclusion === "accepted`
    // unreviewed when `correctiveWork.concluded_dt` is null

    if (cacheStore.corrective_work_on_item.size > 0) {
      for (const correctiveWork of cacheStore.corrective_work_on_item.values()) {
        if (
          correctiveWork.issue === issueId &&
          (correctiveWork.conclusion === "accepted" || !correctiveWork.concluded_dt)
        ) {
          return correctiveWork
        }
      }
    } else {
      const db = await getDB()
      const correctiveWorkOnItemDoc = await db.corrective_work_on_item
        .findOne({
          selector: {
            issue: issueId,
            $or: [{ conclusion: "accepted" }, { concluded_dt: null }],
          },
        })
        .exec()

      if (correctiveWorkOnItemDoc) {
        const correctiveWorkOnItemMutable = correctiveWorkOnItemDoc.toMutableJSON()
        cacheStore.corrective_work_on_item.set(
          correctiveWorkOnItemMutable.id,
          correctiveWorkOnItemMutable
        )

        return correctiveWorkOnItemMutable
      } else {
        const unreviewedCorrectiveWork = await getOpenCorrectiveWorkOnItemFromServer(issueId)

        return (
          unreviewedCorrectiveWork ?? (await getAcceptedCorrectiveWorkOnItemFromServer(issueId))
        )
      }
    }
  } catch (error) {
    Log.error({
      message:
        "An error occurred while fetching CorrectiveWorkOnItem for Issue which is accepted || unreviewed",
      error,
    })
  }
}

export async function hasAcceptedOrUnreviewedCorrectiveWorkOnItemForIssue(
  issueId: string
): Promise<boolean | undefined> {
  try {
    const cacheStore = useCacheStore()

    // Corrective work is;
    // accepted when `correctiveWork.conclusion === "accepted`
    // unreviewed when `correctiveWork.concluded_dt` is null

    if (cacheStore.corrective_work_on_item.size > 0) {
      for (const correctiveWork of cacheStore.corrective_work_on_item.values()) {
        if (
          correctiveWork.issue === issueId &&
          (correctiveWork.conclusion === "accepted" || !correctiveWork.concluded_dt)
        ) {
          return true
        }
      }
    } else {
      const db = await getDB()
      const correctiveWorkOnItemCount = await db.corrective_work_on_item
        .count({
          selector: {
            issue: issueId,
            $or: [{ conclusion: "accepted" }, { concluded_dt: null }],
          },
        })
        .exec()

      return correctiveWorkOnItemCount > 0
    }
  } catch (error) {
    Log.error({
      message:
        "An error occurred while checking IssueOnItem having Accepted || Unreviewed CorrectiveWork",
      error,
    })
  }
}

export async function hasAcceptedCorrectiveWorkOnItemForIssue(
  issueId: string
): Promise<boolean | undefined> {
  try {
    const cacheStore = useCacheStore()

    // Corrective work is;
    // accepted when `correctiveWork.conclusion === "accepted`

    if (cacheStore.corrective_work_on_item.size > 0) {
      for (const correctiveWork of cacheStore.corrective_work_on_item.values()) {
        if (correctiveWork.issue === issueId && correctiveWork.conclusion === "accepted") {
          return true
        }
      }
    } else {
      const db = await getDB()
      const correctiveWorkOnItemCount = await db.corrective_work_on_item
        .count({
          selector: {
            issue: issueId,
            conclusion: "accepted",
          },
        })
        .exec()

      return correctiveWorkOnItemCount > 0
    }
  } catch (error) {
    Log.error({
      message: "An error occurred while checking IssueOnItem having Accepted CorrectiveWork",
      error,
    })
  }
}

export async function hasCorrectiveWorkForIssue(issueId: string): Promise<boolean | undefined> {
  try {
    const cacheStore = useCacheStore()

    if (cacheStore.corrective_work_on_item.size > 0) {
      for (const correctiveWork of cacheStore.corrective_work_on_item.values()) {
        if (correctiveWork.issue === issueId) {
          return true
        }
      }
    } else {
      const db = await getDB()
      const correctiveWorkCount = await db.corrective_work_on_item
        .count()
        .where("issue")
        .eq(issueId)
        .exec()

      return Boolean(correctiveWorkCount)
    }
  } catch (error) {
    Log.error({
      message: "An error occurred while checking IssueOnItem having CorrectiveWork",
      error,
    })
  }
}

async function getOpenCorrectiveWorkOnItemFromServer(issueId: string) {
  try {
    const { getCorrectiveWorkOnItemList } = await import("src/util/http/csi/performedWork")
    const filterParams = {
      issue__id: issueId,
      concluded_dt__isnull: "True",
    }
    const correctiveWork = await getCorrectiveWorkOnItemList({ filterParams })
    return correctiveWork.results[0]
  } catch (error) {
    Log.error({
      message:
        "An error occurred while getting CorrectiveWork for IssueOnItem from server where concluded_dt is null",
      error,
    })
  }
}

async function getAcceptedCorrectiveWorkOnItemFromServer(issueId: string) {
  try {
    const { getCorrectiveWorkOnItemList } = await import("src/util/http/csi/performedWork")
    const mainParams = {
      page: 1,
      page_size: 1,
    }
    const filterParams = {
      issue__id: issueId,
      conclusion: "accepted",
    }

    const correctiveWork = await getCorrectiveWorkOnItemList({ mainParams, filterParams })
    return correctiveWork.results[0]
  } catch (error) {
    Log.error({
      message:
        "An error occurred while getting accepted CorrectiveWorkOnItem for Issue from server",
      error,
    })
  }
}

/**
 * Returns whether the given Issue is in raised state or not
 *
 * SA: Definition of "Issue in raised state" =>
 *      concluded_dt === null &&
 *      corrective_work_order === null &&
 *      No CorrectiveWork exists for the given Issue where conclusion !== "rejected"
 *
 * @param issue
 */
export async function isIssueInRaisedState(issue: IssuesOnItem) {
  const issueClosed = issue.concluded_dt === null
  const issueInWorkOrder = issue.corrective_work_order === null

  return (
    issueClosed &&
    issueInWorkOrder &&
    !(await hasAcceptedOrUnreviewedCorrectiveWorkOnItemForIssue(issue.id))
  )
}

/**
 * Attempts to reject an issue.
 * If the user does not approve of that, it returns nothing.
 * If the user approves the rejection, it returns the issue if it is successful, or null if not.
 * Shows notifications on various outcomes, including failure and success.
 *
 * @param issue
 */
export async function rejectIssue(issue: IssuesOnItem): Promise<RejectIssueData | null | void> {
  const userIsAllowed = await canIssueBeRejectedOrSuperseded(issue, true)
  if (!userIsAllowed) return

  const rejectReason = await rejectIssueDialog()
  if (!rejectReason) return

  const { t } = i18n.global

  const undoMessage = t("notifications.issue_rejected")
  return simpleUndoNotification(undoMessage, () => rejectIssueOperations(issue.id, rejectReason))
}

/**
 * Patches the issue with relevant closing statement data.
 * It then updates the DB and cache store.
 * Finally, a comment with a closing statement is sent.
 *
 * Returns the updated issue on success and null on fail.
 * Shows notifications in either scenario.
 *
 * @param issueId
 * @param rejectReason
 */
async function rejectIssueOperations(
  issueId: string,
  rejectReason: string
): Promise<RejectIssueData | null> {
  const { t } = i18n.global
  const userStore = useUserStore()
  const cacheStore = useCacheStore()

  try {
    // First we need to send the rejecting comment since we cannot comment after issue is closed

    // Get the reject reason as a comment
    const issueComment = await saveIssueOnItemComment(issueId, rejectReason)

    // Update the Issue

    const issue: PayloadEntity<Partial<IssuesOnItem>> = {
      concluded_dt: new Date().toJSON(),
      conclusion: "rejected",
      concluded_by_user: userStore.id,
      corrective_work_order: null,
    }

    const updatedIssue = await patchIssuesOnItem({}, issueId, issue)

    if (updatedIssue) {
      const db = await getDB()
      await db.issues_on_item.upsert(updatedIssue)

      // Manually update the issue card data
      const issueCardData = await getIssueCardData(updatedIssue)
      cacheStore.issueCard.set(issueId, issueCardData)

      if (issueComment) {
        showSuccessNotification(t("notifications.issue_rejected_success"))

        return {
          rejectedIssue: updatedIssue,
          rejectingComment: issueComment,
        }
      }
    }
  } catch (error) {
    showErrorNotification(t("sfxErrorMsg.error_rejecting_issue"))
    Log.error({ message: "An error occurred during issue rejection data operations", error })
  }

  return null
}

/**
 * Check whether an Issue can be Rejected or Superseded
 *
 * @param issue The issue which gonna be Rejected or Superseded
 * @param rejectOperation A boolean to specify why this function is called for
 *                        Whether to Reject an Issue or to Supersede an Issue
 *                          When Rejecting an Issue
 *                              rejectOperation -> True
 *                          When Superseding an Issue
 *                              rejectOperation -> False
 */
export async function canIssueBeRejectedOrSuperseded(
  issue: IssuesOnItem,
  rejectOperation: boolean
): Promise<boolean> {
  const { t } = i18n.global

  if (!userHasPermissionToUpdateIssue(issue)) {
    showWarningNotification(t("permissionNotifications.no_access"))
    return false
  }

  if (issue.concluded_dt !== null) {
    showWarningNotification(t("notifications.issue_closed"), true)
    return false
  }

  if (issue.corrective_work_order && !userHasPermissionToUpdateWorkOrder(issue.work_domain)) {
    showWarningNotification(t("permissionNotifications.issue_in_work_order"))
    return false
  }

  const acceptedCorrectiveWork = await hasAcceptedCorrectiveWorkOnItemForIssue(issue.id)

  if (acceptedCorrectiveWork) {
    showWarningNotification(t("notifications.issue_has_accepted_corrective_work"), true)

    return false
  } else if (await getOpenCorrectiveWorkOnItemForIssue(issue.id)) {
    const operation = rejectOperation ? "reject" : "supersede"

    await workMustBeReviewedDialog(operation)
    return false
  } else {
    return true
  }
}

export function userHasPermissionToDeleteIssue(issue: IssuesOnItem): boolean {
  const userStore = useUserStore()

  const userHasPermission = userStore.checkUserPermission(
    PERMISSION_ISSUES,
    PERMISSION_ACCESS_DELETE,
    { workDomainUUID: issue.work_domain }
  )

  if (userHasPermission && userStore.id === issue.raised_by_user) {
    return true
  } else {
    const { t } = i18n.global
    showWarningNotification(t("permissionNotifications.no_access"))
    return false
  }
}

/**
 * Attempts to close an issue.
 *
 * User needs to have UPDATE permissions for Issue.
 * Additionally, if the issue is a part of a Work Order, the user would also need UPDATE permissions for Work Order.
 *
 * Depending on if the issue is corrected or not, it displays the appropriate dialog.
 * Then it triggers an undo notification, allowing the user to undo their action.
 *
 * If the undo action is taken, then it returns undefined.
 * Else it will attempt to close the issue and submit a comment if one is written.
 * Then it returns the ClosedIssueData or NULL if it failed for whatever reason.
 *
 * @param issue
 */
export async function closeIssue(issue: IssuesOnItem): Promise<ClosedIssueData | null | undefined> {
  const { t } = i18n.global

  if (!userHasPermissionToUpdateIssue(issue)) {
    showWarningNotification(t("permissionNotifications.no_access"))
    return
  }

  if (issue.corrective_work_order && !userHasPermissionToUpdateWorkOrder(issue.work_domain)) {
    showWarningNotification(t("permissionNotifications.issue_in_work_order"))
    return
  }

  const unreviewedCorrectiveWork = await getOpenCorrectiveWorkOnItemForIssue(issue.id)

  if (unreviewedCorrectiveWork) {
    await workMustBeReviewedDialog("close")
    return
  }

  const correctiveWork = await getAcceptedOrUnreviewedCorrectiveWorkOnItemForIssue(issue.id)

  const closingComment =
    correctiveWork?.conclusion === "accepted"
      ? await closeIssueCorrectedDialog()
      : await closeIssueNotCorrectedDialog()

  // If the user presses cancel, then it returns null. In any other case it would return a string, even an empty one.
  if (closingComment === null) return

  const undoMessage = correctiveWork
    ? t("notifications.closed_corrected_issue")
    : t("notifications.closed_issue")

  return simpleUndoNotification(undoMessage, () =>
    closeIssueOperations(issue, correctiveWork, closingComment)
  )
}

export async function closeIssueOperations(
  issue: IssuesOnItem,
  correctiveWork: CorrectiveWorkOnItem | undefined,
  closingComment: string | null
): Promise<ClosedIssueData | null> {
  const { t } = i18n.global

  try {
    const userStore = useUserStore()
    const currentDate = new Date().toJSON()
    const cacheStore = useCacheStore()

    // If the use gave a reason for closing the Issue, save it as a IssueComment
    let issueComment
    if (closingComment) issueComment = await saveIssueOnItemComment(issue.id, closingComment)

    const modifiedIssue: Partial<IssuesOnItem> = {
      concluded_dt: currentDate,
      conclusion: correctiveWork?.conclusion === "accepted" ? "closed_resolved" : "closed",
      concluded_by_user: userStore.id,
    }

    if (correctiveWork?.conclusion !== "accepted") {
      modifiedIssue.corrective_work_order = null
    }

    const closedIssue = await patchIssuesOnItem({}, issue.id, modifiedIssue)
    const db = await getDB()
    await db.issues_on_item.upsert(closedIssue)

    const issueCardData = await getIssueCardData(closedIssue)
    cacheStore.issueCard.set(closedIssue.id, issueCardData)

    showSuccessNotification(t("notifications.issue_closed_success"))

    return {
      closedIssue: closedIssue,
      closingComment: issueComment,
      correctiveWork: correctiveWork,
    }
  } catch (error) {
    showErrorNotification(t("sfxErrorMsg.error_closing_issue"))
    Log.error({ message: "An error occurred during issue closing data operations", error })
  }

  return null
}

/**
 * Check that there is a valid work domain to raise an issue within.
 */
export async function hasWorkDomainToRaiseIssue(item: Item | undefined): Promise<boolean> {
  //
  // Otherwise we need to disable raise issue.
  const workDomainDocuments = await getWorkDomainWorkspaceListData()
  const issuePriorityDocuments = await getIssuePriorityListData()
  const issuePriorityData = issuePriorityDocuments.map((element) => element.toJSON())
  const validWorkDomainOptions = await filterInvalidWorkDomains(
    workDomainDocuments,
    item,
    issuePriorityData
  )
  return validWorkDomainOptions.length > 0
}

export async function deleteIssue(issue: IssuesOnItem) {
  try {
    if (!userHasPermissionToDeleteIssue(issue)) return

    const { t } = i18n.global

    if (issue.concluded_dt !== null) {
      showWarningNotification(t("notifications.issue_closed"), true)
      return
    }

    // Check if any other user interacted with the Issue other than the one who raised it
    const userStore = useUserStore()
    const issueHistory = await getEntityHistory({}, "issues/onitem", issue.id)
    const hasMoreThanOneUser = issueHistory.results.some((entry) => entry.user_id !== userStore.id)

    if (hasMoreThanOneUser) {
      showWarningNotification(t("notifications.issue_has_multiple_user_interactions"))
      return
    }

    const acceptedCorrectiveWork = await hasAcceptedCorrectiveWorkOnItemForIssue(issue.id)

    if (acceptedCorrectiveWork) {
      showWarningNotification(t("notifications.issue_has_accepted_corrective_work"), true)
    } else if (await getOpenCorrectiveWorkOnItemForIssue(issue.id)) {
      await workMustBeReviewedDialog("delete")
    } else {
      // TODO: Implement the Issue delete process
      showNotImplementedNotification()
    }
  } catch (error) {
    Log.error({ message: "An error occurred while trying to delete the Issue", error: error })
  }
}

/**
 * Returns the old issue which the new issue got superseded.
 * If not available, returns undefined.
 *
 * @param issueId
 */
export async function getSupersededIssueData(issueId: string): Promise<IssuesOnItem | undefined> {
  try {
    const db = await getDB()
    const issueInDb = await db.issues_on_item
      .findOne({
        selector: {
          superseded_by: issueId,
        },
      })
      .exec()

    if (issueInDb) {
      return issueInDb.toMutableJSON()
    } else {
      const { getIssuesOnItemList } = await import("src/util/http/csi/issues")
      const filterParams = {
        superseded_by: issueId,
      }
      const issueInServer = await getIssuesOnItemList({ filterParams })
      return issueInServer.results[0]
    }
  } catch (error) {
    Log.error({
      message: "An error occurred while fetching the old issue which the new issue got superseded",
      error,
      data: { issueId },
    })
  }
}

export async function saveIssueOnItemComment(
  issueId: string,
  commentText: string,
  commentImages?: AttachmentUpload[],
  reaffirmed?: boolean
): Promise<IssueComment | undefined> {
  const userStore = useUserStore()

  try {
    const comment: IssueCommentPayload = {
      text: commentText,
      reaffirmed: Boolean(reaffirmed),
      user: userStore.id,
      issue: issueId,
    }

    const commentInServer = await postIssueOnItemComment({}, comment)

    if (commentInServer) {
      if (commentImages && commentImages.length > 0) {
        const { uploadImages } = await import("src/util/functions/attachments")

        commentInServer.attachments =
          (await uploadImages("issue_item_comment", commentInServer.id, commentImages)) || []
      }

      const db = await getDB()
      await db.issue_on_item_comments.insert(commentInServer)

      return commentInServer
    }
  } catch (error) {
    const { t } = i18n.global
    showErrorNotification(t("sfxErrorMsg.error_adding_comment"))

    Log.error({
      message: "An error occurred while saving the IssueOnItem comment/attachments",
      error,
      data: { issueId: issueId },
    })
  }
}

export async function getIssueCommentData(commentId: string): Promise<IssueComment | undefined> {
  try {
    const cacheStore = useCacheStore()
    const issueCommentInCache = cacheStore.issue_on_item_comments.get(commentId)

    if (issueCommentInCache) {
      return issueCommentInCache
    } else {
      const db = await getDB()
      const commentInDb = await db.issue_on_item_comments.findOne(commentId).exec()

      if (commentInDb) {
        const mutableComment = commentInDb.toMutableJSON()
        cacheStore.issue_on_item_comments.set(mutableComment.id, mutableComment)
        return mutableComment
      } else {
        return await getIssueOnItemComment({}, commentId)
      }
    }
  } catch (error) {
    Log.error({
      message: "An error occurred while fetching IssueOnItemComment data for a given commentId",
      error,
      data: { commentId: commentId },
    })
  }
}

export async function getIssueCommentListForIssueOnItem(
  issueId: string
): Promise<IssueComment[] | undefined> {
  try {
    const cacheStore = useCacheStore()
    let commentList: IssueComment[] = []

    if (cacheStore.issue_on_item_comments.size > 0) {
      for (const commentInCache of cacheStore.issue_on_item_comments.values()) {
        if (commentInCache.issue === issueId) {
          commentList.push(commentInCache)
        }
      }
    } else {
      const db = await getDB()
      const commentsInDb = await db.issue_on_item_comments
        .find({ selector: { issue: issueId } })
        .exec()

      if (commentsInDb.length > 0) {
        for (const comment of commentsInDb) {
          const commentMutable = comment.toMutableJSON()
          cacheStore.issue_on_item_comments.set(commentMutable.id, commentMutable)
          commentList.push(comment)
        }
      } else {
        const commentsInServer = await getIssueOnItemCommentList({
          mainParams: undefined,
          filterParams: { issue: issueId },
        })
        commentList = commentsInServer.results
      }
    }
    return commentList.sort((a, b) => b.created_dt.localeCompare(a.created_dt))
  } catch (error) {
    Log.error({
      message: "An error occurred while fetching IssueOnItemComment data for the given Issue",
      error,
      data: { issueId: issueId },
    })
  }
}

export async function getCommentForClosedIssueOnItem(issueId: string) {
  const issueCommentList = await getIssueCommentListForIssueOnItem(issueId)

  // Since the comment section get disabled for 'concluded' Issues,
  // we can take the last comment as the closing comment
  if (issueCommentList && issueCommentList.length > 0) return issueCommentList[0].text
}

export async function getIssueOnItemActionForIssue(issueId: string) {
  try {
    const cacheStore = useCacheStore()
    if (cacheStore.issue_on_item_actions.size > 0) {
      for (const actionInCache of cacheStore.issue_on_item_actions.values()) {
        if (actionInCache.issue === issueId) {
          return actionInCache
        }
      }
    } else {
      const db = await getDB()
      const actionInDb = await db.issue_on_item_actions
        .findOne({
          selector: {
            issue: issueId,
          },
        })
        .exec()

      if (actionInDb) {
        const actionMutable = actionInDb.toMutableJSON()
        cacheStore.issue_on_item_actions.set(actionMutable.id, actionMutable)
        return actionMutable
      } else {
        const actionInList = await getIssuesOnItemActionsList({
          mainParams: undefined,
          filterParams: { issue: issueId },
        })
        return actionInList.results[0]
      }
    }
  } catch (error) {
    Log.error({
      message: "An error occurred while fetching IssueOnItemAction data for Issue",
      error,
    })
  }
}

export async function getIssuesOnItemForCorrectiveWorkOrder(
  workOrderId: string
): Promise<IssuesOnItem[]> {
  const cacheStore = useCacheStore()

  if (cacheStore.issues_on_item.size > 0) {
    const issuesInCache = cacheStore.issues_on_item.values()
    const issuesForWorkOrder: IssuesOnItem[] = []

    for (const issue of issuesInCache) {
      if (issue.corrective_work_order === workOrderId) {
        issuesForWorkOrder.push(issue)
      }
    }

    return issuesForWorkOrder
  } else {
    const db = await getDB()
    const issues = await db.issues_on_item
      .find({ selector: { corrective_work_order: workOrderId } })
      .exec()

    return issues.map((i) => i.toMutableJSON())
  }
}

// From left to right, the icon order is:
// 1. Issue Priority
// 2. Work Order priority
// 3. Performed Corrected Work / Accepted Performed Corrective Work
export function getIssueCardIcons(
  issuePriority?: IssuePriority,
  workOrderPriority?: WorkOrderPriority,
  correctiveWork?: CorrectiveWorkOnItem
): IconRowElement[] {
  const isCorrected = Boolean(correctiveWork)
  const isAccepted = correctiveWork?.conclusion === "accepted"

  return [
    {
      name: Icons.ISSUE,
      color: Boolean(issuePriority) ? getIssuePriorityColor(issuePriority!) : undefined,
    },
    {
      name: Icons.WORK_ORDERS,
      color: Boolean(workOrderPriority) ? getWorkOrderPriorityColor(workOrderPriority!) : undefined,
    },
    {
      name: isAccepted ? Icons.ACCEPT : Icons.WORK_CORRECTIVE,
      color: !isCorrected ? undefined : isAccepted ? "positive" : "secondary",
    },
  ]
}

/**
 * Removes an issue from its work order by patching the issue
 * .
 * @param issue The issue to be removed from its work order.
 */
export async function removeIssueFromWorkOrder(
  issue: IssuesOnItem
): Promise<IssuesOnItem | undefined> {
  const { t } = i18n.global

  try {
    const updatedIssue = await patchIssuesOnItem({}, issue.id, {
      corrective_work_order: null,
    })
    if (updatedIssue) {
      const db = await getDB()
      await db.issues_on_item.upsert(updatedIssue)
    }
    showSuccessNotification(t("notifications.issue_removed_from_work_order"))
    return updatedIssue
  } catch (error) {
    showErrorNotification(t("sfxErrorMsg.error_update_issue"))
    Log.error({ message: "An error occurred when removing issue from its work order.", error })
  }

  return undefined
}

export async function updateIssueOnItemWithNewWorkOrderId(issueId: string, workOrderId: string) {
  try {
    const data = await patchIssuesOnItem({}, issueId, { corrective_work_order: workOrderId })

    const db = await getDB()
    await db.issues_on_item.upsert(data)

    return data
  } catch (error) {
    const { t } = i18n.global
    showErrorNotification(t("sfxErrorMsg.error_updating_issue_on_item"))
    Log.error({
      message: "An error occurred while updating the IssueOnItem in IssueContainer",
      error,
    })
  }
}

export async function getIssuesOnItemForItem(itemId: string) {
  try {
    const db = await getDB()
    const data = await db.issues_on_item.find({ selector: { actionable_item: itemId } }).exec()
    const sortedData = data.sort((a, b) => b.created_dt.localeCompare(a.created_dt))
    return sortedData.map((item) => item.toMutableJSON())
  } catch (error) {
    Log.error({
      message: "An error occurred while fetching the list of IssueOnItems for a given Item id",
      error,
    })
  }
}

//region Issue Table

export async function toIssueTableData(
  issueRaw: Issue | IssueDocument
): Promise<RowData<IssueTableData>> {
  const issue = isRxDocument(issueRaw) ? (issueRaw as IssueDocument).toMutableJSON() : issueRaw
  const priorityData = await getIssuePriorityData(issue.priority)
  const actionableItem = await getItemData(issue.actionable_item)
  const correctiveWorkOrderData = issue.corrective_work_order
    ? await getWorkOrderCorrectiveItemData(issue.corrective_work_order)
    : undefined

  if (!priorityData) throw new Error("Missing Issue Priority Data")

  const data: RowData<IssueTableData> = {
    issueData: issue,
    priorityData: priorityData,
    actionableItem: actionableItem,
    workDomainData: await getWorkDomainWorkspaceData(issue.work_domain),
    correctiveWork: await getAcceptedOrUnreviewedCorrectiveWorkOnItemForIssue(issue.id),
    correctiveWorkOrderData: correctiveWorkOrderData,
    overdueTime: getOverdueTime(issue, priorityData),
    itemClassData: actionableItem ? await getItemClassData(actionableItem?.item_class) : undefined,
    regularWork: issue.raised_from ? await getRegularWorkOnItemData(issue.raised_from) : undefined,
    workOrderPriorityData: correctiveWorkOrderData
      ? await getWorkOrderPriorityData(correctiveWorkOrderData.priority)
      : undefined,
    link: router.resolve({
      name: NamedManagerPath.ISSUE,
      params: { issueId: issue.id },
    }).href,
  }

  if (issue.main_image) {
    data.rowImage = { uuid: issue.main_image }
  }

  data.status = await addIssueTableStatusData(data)

  return data
}

async function addIssueTableStatusData(issue: IssueTableData) {
  const db = await getDB()
  const correctiveWork = await db.corrective_work_on_item
    .findOne({
      selector: {
        issue: issue.issueData.id,
        conclusion: { $ne: "rejected" },
      },
    })
    .exec()

  const workOrderIcon: IconTableRowElement = {
    column: "work-order",
    sortData: null,
    name: Icons.WORK_ORDERS,
    color: "transparent",
  }

  if (issue.workOrderPriorityData) {
    workOrderIcon.color = getWorkOrderPriorityColor(issue.workOrderPriorityData)
    workOrderIcon.sortData = issue.workOrderPriorityData.rank
  }

  const correctiveWorkStatus: IconTableRowElement = {
    column: "corrective-work-accepted",
    sortData: 0,
    color: "transparent",
  }

  if (correctiveWork?.conclusion) {
    correctiveWorkStatus.color = "transparent"

    if (correctiveWork?.conclusion === "accepted") {
      correctiveWorkStatus.color = "positive"
      correctiveWorkStatus.name = Icons.ACCEPT
      correctiveWorkStatus.sortData = 1
    } else if (correctiveWork?.conclusion === "rejected") {
      correctiveWorkStatus.color = "fun-label-and-icon"
      correctiveWorkStatus.name = Icons.REJECT_CIRCLE
      correctiveWorkStatus.sortData = -1
    }
  }

  const status: IconTableRowElement[] = []

  status.push(
    {
      column: "issue-overdue",
      // Negative overdue means this still has time to be completed
      sortData: issue.overdueTime && issue.overdueTime > 0 ? issue.overdueTime : null,
      name: Icons.WARNING,
      color: Boolean(issue.overdueTime && issue.overdueTime > 0) ? "negative" : "transparent",
    },
    workOrderIcon,
    {
      column: "corrective-work",
      sortData: Number(correctiveWork !== null),
      name: Icons.WORK_CORRECTIVE,
      color: Boolean(correctiveWork) ? "fun-label-and-icon" : "transparent",
    },
    correctiveWorkStatus,
    {
      column: "closed",
      sortData: Number(issue.issueData.concluded_dt !== null),
      ignoreRendering: true,
    }
  )

  return status
}

//endregion
